import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TwCompaniesModal from '../../Companies/CompaniesModal/TwCompaniesModal';
import TwLoadCommodities from '../../../components/Commodities/TwLoadCommodities/TwLoadCommodities';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TwAdvancedRates from '../../../components/TwAdvancedRates/TwAdvancedRates';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import useMediaQuery from '@mui/material/useMediaQuery';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { ReactComponent as InfoIcon } from '../../../assets/info.svg';
import { SxProps, Theme } from '@mui/material/styles';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import useAuth from '../../../providers/Auth/Auth.provider';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { IntegrationsTypes } from '../../Settings/Integrations/Integrations';
import { CreateLoadFields, LOAD_TYPE } from '../../../redux/api/load/load.model';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import useScrollToView from '../../../hooks/useScrollToView';
import {
  DEFAULT_LOAD_RATE_UI_ID,
  addCustomer,
  setRate,
  setSendLoadUpdate,
  setShipperLoadNumber,
  toggleAdvancedRates,
  toggleBrokeredLoad,
  updateInitialLoadRates,
  updateInstructions,
  updateLoadNotificationSettings,
  updateLoadRates,
  updatePayableAmount,
  updateSelectedContact,
  updateSelectedCustomer,
  updateSelectedDispatcher,
  updateSelectedDivision,
  updateQuickbooksEnabled,
  setBookingType,
  setRateTableId,
  getEmptyLoadRateServiceRow,
  SliceOrderRate,
  updateDriverPayConfirmationModal
} from '../../../redux/features/orders/ordersSlice';
import { IDivisionsResponse } from '../../../redux/api/divisions/divisions.model';
import { CompaniesEntities } from '../../Companies/Companies';
import { useGetCreateLoadConfigQuery } from '../../../redux/api/load/loadApi';
import { useGetInvoiceServicesQuery } from '../../../redux/api/invoiceService/invoiceServiceApi';
import { useGetDivisionsQuery } from '../../../redux/api/divisions/divisionsApi';
import { useFormikContext } from 'formik';
import {
  InvoiceService,
  InvoiceServiceUnit,
  LINEHAUL_FLAT_RATE_NAME
} from '../../../redux/api/invoiceService/invoiceService.model';
import useTwClient from '../../../hooks/useTwCustomer';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import IconButton from '@mui/material/IconButton';
import TwCustomerMenu from '../../../components/TwCustomerMenu/TwCustomerMenu';
import { ClientResponse, IContactClient } from '../../../redux/api/customer/customer.model';
import { LoadNotificationSettingsRequest } from '../../../providers/Settings/LoadNotificationSettings.model';
import CreateClientContact from '../../Companies/CompaniesModal/CreateClientContact';
import EditClientContact from '../../Companies/Customers/CreateClient/EditClientContact';
import TwEditLoadNotificationSettings, {
  TwLoadNotificationValues
} from '../../../components/TwEditLoadNotificationSettings/TwEditLoadNotificationSettings';

import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import {
  selectIsCreateLoadFromFileOpen,
  selectIsCreateLoadOpen,
  selectIsCreateOrderOpen,
  setAnchorId
} from '../../../redux/features/navigation/navigationSlice';
import LoadUtils from '../../../utils/loadUtils';
import Grid from '@mui/material/Grid';
import { useGetDispatchersQuery } from '../../../redux/api/dispatchers/dispatcherApi';
import { DispatcherResponse } from '../../../redux/api/dispatchers/dispatcher.model';
import CreateDispatcher from '../../Dispatchers/CreateDispatcher/CreateDispatcher';
import EditDispatcher from '../../Dispatchers/EditDispatcher/EditDispatcher';
import { useGetOrderNotificationSettingsQuery } from '../../../redux/api/orders/ordersApi';
import { useGetTenantAccountingSettingsQuery } from '../../../redux/api/tenant/tenantApi';
import { EntityType } from '../../../redux/Global.model';
import { BOOKING_TYPE } from '../../../redux/api/orders/orders.model';
import TwRadioButton from '../../../components/TwRadioButtons/TwRadioButtons';
import DisabledBox from '../../../components/TwAdvancedRates/DisabledBox';
import NumberUtils from '../../../utils/numberUtils';
import { RowData } from '../../../components/TwAdvancedRates/RateRow';
import {
  TwEmptyRateTableSelector,
  TwRateTableSelector
} from '../../../components/TwRateTableSelector/TwRateTableSelector';
import {
  RateTableStatus,
  SearchRateTablesResponseDto
} from '../../../redux/api/rateTable/rateTable.model';
import { nanoid } from '@reduxjs/toolkit';
import { useSearchRateTablesQuery } from '../../../redux/api/rateTable/rateTableApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import FuelSurchargeTableUtils from '../../../utils/fuelSurchargeTableUtils';
import InvoiceUtils from '../../../utils/invoiceUtils';
import { LoadingInvoiceServiceModal } from '../../../components/TwLoadingInvoiceServiceModal/TwLoadingInvoiceServiceModal';
import { useLazyGetDieselPriceDataQuery } from '../../../redux/api/fuelSurchargeTable/fuelSurchargeTableApi';
import {
  Frequency,
  FuelPriceType
} from '../../../redux/api/fuelSurchargeTable/fuelSurchargeTable.model';
import { LoadingEiaPriceModal } from '../../Invoices/FuelSurchargeTable/CreateEditModal/LoadingEiaPriceModal';
import useCheckDriverPay from '../../../hooks/useCheckDriverPay';
import TwSwitch from '../../../components/TwSwitch/TwSwitch';

interface OrderInfoFormValues {
  shipper_rate: number | null | '';
  rateOptionalEnable: boolean;
  customer_order_number: string;
  division: TwAutocompleteOption | null | '';
  customer: TwAutocompleteOption | null | '';
  dispatcher: TwAutocompleteOption | null;
  customerOrderNumber: string;
  instructions: string;
  rateTableId?: string;
}

interface OrderInfoProps {
  isImport?: boolean;
  fullScreenImport?: boolean;
  createOrder?: boolean;
  className?: string;
  divisionId?: string;
  loading?: boolean;
  dataContainerSx?: SxProps;
  divisionEditable?: boolean;
  initialPickupDate?: string | null;
  showRateTables?: boolean;
  isParentModalOpen?: boolean;
}

const DivRateTableSx: SxProps = {
  width: {
    xs: '100%',
    sm: '100%',
    md: '400px',
    lg: '500px',
    xl: '500px'
  }
};

const isValidDate = (dateString: string | Date | null): boolean => {
  if (!dateString) return false;

  const date = dateString instanceof Date ? dateString : new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

const OrderInfo = forwardRef<HTMLDivElement, OrderInfoProps>(
  (
    {
      isImport = false,
      fullScreenImport,
      createOrder = false,
      className,
      divisionId,
      loading,
      dataContainerSx,
      divisionEditable = true,
      initialPickupDate,
      showRateTables = false,
      isParentModalOpen
    },
    ref
  ) => {
    // hooks, refs, settings
    const { data } = useAuth();
    const { addNotification } = useNotifications();
    const userData = data?.userData;
    const enableLtl = userData?.tenant?.ltl_enabled || false;
    const orderNotificationSavedSettings = useAppSelector(
      (state) => state.orders.order_notification_settings
    );
    const [scrollToView] = useScrollToView();
    const dispatch = useAppDispatch();
    const { enableShowCustomerPreferredCurrencyCode } = useFeatureFlags();

    const { data: savedOrderNotificationSettings } = useGetOrderNotificationSettingsQuery({});

    const loadState = useAppSelector((state) => state.orders);
    const anchorId = useAppSelector((state) => state.navigation.anchorId);
    const isEditLoadOpen = useAppSelector((state) => state.navigation.isEditLoadOpen);
    const isCreateOrderOpen = useAppSelector(selectIsCreateLoadOpen);
    const isCreateCustomerOrderOpen = useAppSelector(selectIsCreateOrderOpen);
    const isCreateLoadFromFileOpen = useAppSelector(selectIsCreateLoadFromFileOpen);
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const anyDriverPayExists = useCheckDriverPay({ entity: EntityType.ORDER });
    const rateRef = useRef<HTMLElement | null>(null);

    const rateOptionalEnable =
      (userData?.tenantSettings && userData.tenantSettings['rate-optional']) || false;
    const divisionsEnabled =
      (userData?.tenantSettings && userData.tenantSettings['divisions']) || false;
    const brokeredEnabled =
      (userData?.tenantSettings && userData.tenantSettings['brokered-loads']) || false;
    const loadCommoditiesEnabled =
      (userData?.tenantSettings && userData.tenantSettings['load-commodities']) || false;
    const dispatcherEnable =
      (userData?.tenantSettings && userData.tenantSettings['multiple-dispatchers']) || false;
    const rateTablesEnabled =
      (userData?.tenantSettings && userData.tenantSettings['use_rate_table_for_orders']) || false;

    const showAdvancedRates = loadState.show_advanced_rates;
    const initialLoadRates = loadState.initial_load_rates;
    const advancedRatesMiles = loadState.total_automatic_mileage || 0; // Independent miles from other calculated mileage / loaded miles
    const providerContact = loadState.provider_contact;
    const loadId = loadState.order_id;
    const sendLoadUpdate = loadState.send_order_update;
    const providerCustomer = loadState.provider_customer;
    const loadType = loadState.order_type;
    const trips = loadState.trips;

    const { values, setFieldValue, errors, setFieldError } =
      useFormikContext<OrderInfoFormValues>();

    // states
    const [pickupDate, setPickupDate] = useState<Date | null>(
      initialPickupDate && isValidDate(initialPickupDate) ? new Date(initialPickupDate) : null
    );
    const [initialShipperLoadNumber, setInitialShipperLoadNumber] = useState<
      string | null | undefined
    >(undefined);
    const [initialShipperRate, setInitialShipperRate] = useState<number | undefined>(undefined);
    const [requiredFields, setRequiredFields] = useState<CreateLoadFields[] | never>([]);
    const [selectedDivisionId, setSelectedDivisionId] = useState<string | undefined>(divisionId);
    const [selectedDivision, setSelectedDivision] = useState<
      TwAutocompleteOption | null | undefined
    >(null);
    const [payableAmount, setPayableAmount] = useState<number | undefined>(undefined);
    const [defaultQboServiceId, setDefaultQboServiceId] = useState<string | null>(null);
    const [isCreateDivisionOpen, setIsCreateDivisionOpen] = useState<boolean>(false);
    const [customers, setCustomers] = useState<TwAutocompleteOption[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showContactModal, setShowContactModal] = useState<boolean>(false);
    const [clientToCreateContact, setClientToCreateContact] = useState<null | ClientResponse>(null);
    const [editContactOpen, setEditContactOpen] = useState<boolean>(false);
    const [contactToEdit, setContactToEdit] = useState<null | {
      customer: ClientResponse;
      id: string;
    }>(null);
    const [blockFetchCustomers, setBlockFetchCustomers] = useState<boolean>(false);
    const [createCustomerDialogOpen, setCreateCustomerDialogOpen] = useState(false);
    const [isFirstContact, setIsFirstContact] = useState<boolean>(false);
    const [editClientOpen, setEditClientOpen] = useState<boolean>(false);
    const [editLoadNotificationSettingsOpen, setEditLoadNotificationSettingsOpen] =
      useState<boolean>(false);
    const [selectedCustomer, setSelectedCustomer] = useState<
      TwAutocompleteOption | null | undefined
    >(null);
    const [sendLoadUpdateChecked, setSendLoadUpdateChecked] = useState<boolean>(false);
    const [dispatchers, setDispatchers] = useState<TwAutocompleteOption[]>([]);
    const [firstRender, setFirstRender] = useState<boolean>(true);
    const [selectedDispatcher, setSelectedDispatcher] = useState<
      TwAutocompleteOption | null | undefined
    >(null);
    const [selectedDispatcherId, setSelectedDispatcherId] = useState<string>('');
    const [editDispatcherOpen, setEditDispatcherOpen] = useState<boolean>(false);
    const [createDispatcherDialogOpen, setCreateDispatcherDialogOpen] = useState(false);
    const [isQuickbooksEnabledForSelectedDivision, setIsQuickbooksEnabledForSelectedDivision] =
      useState<boolean>(false);
    const [selectedRateTableData, setSelectedRateTableData] = useState<
      SearchRateTablesResponseDto | undefined
    >(undefined);
    const [openLoadingEiaPriceModal, setOpenLoadingEiaPriceModal] = useState<boolean>(false);
    const [isDieselPriceDataFetching, setIsDieselPriceDataFetching] = useState<boolean>(false);
    const [isDieselPriceDataError, setIsDieselPriceDataError] = useState<boolean>(false);

    // RTK Queries calls
    const { data: dispatchersData = { content: [], total: 0 } } = useGetDispatchersQuery(
      {},
      { skip: !dispatcherEnable }
    );
    const { clients: twClients, fetchClients } = useTwClient(true);
    const { data: createLoadConfig = [] } = useGetCreateLoadConfigQuery();
    const {
      data: invoiceServicesData,
      isError: invoiceServicesError,
      isLoading: invoiceServicesLoading,
      isFetching: invoiceServicesFetching
    } = useGetInvoiceServicesQuery(
      {
        filters: {
          divisionId: selectedDivisionId || ''
        }
      },
      { skip: !selectedDivisionId }
    );
    const { data: divisionsData } = useGetDivisionsQuery(
      {},
      {
        selectFromResult: ({ data = { content: [], total: 0 } }) => ({
          data: {
            content: data.content.map((division) => ({
              id: division.id,
              label: division.name,
              value: division
            })),
            total: data.total
          }
        })
      }
    );
    const { data: accountingSettings } = useGetTenantAccountingSettingsQuery(
      {
        divisionId: divisionId || userData?.defaultDivision.id || ''
      },
      { skip: !divisionId && !userData?.defaultDivision.id }
    );
    const { data: rateTablesData, isLoading: isRateTableLoading } = useSearchRateTablesQuery(
      {
        filters: {
          customer_id: selectedCustomer?.id || '',
          status: RateTableStatus.ACTIVE,
          pickup_date: pickupDate ? pickupDate.toISOString() : undefined,
          division_id: loadState.selected_division || userData?.defaultDivision.id || ''
        }
      },
      {
        skip:
          !selectedCustomer?.id ||
          (!loadState.selected_division && !userData?.defaultDivision.id) ||
          loading
      }
    );
    const [getDieselPriceData] = useLazyGetDieselPriceDataQuery();

    const quickbooksEnabled = !!accountingSettings?.division_qbo_credentials?.id;

    // #region Functions
    const onClientSelected = (
      client: TwAutocompleteOption | null,
      clearDriverPay = false
    ): void => {
      setSelectedCustomer(client);
      if (anyDriverPayExists && clearDriverPay) {
        dispatch(
          updateDriverPayConfirmationModal({
            open: true,
            action: () =>
              dispatch(
                updateSelectedCustomer({
                  customer: client?.value ? (client.value as ClientResponse) : undefined,
                  clearDriverPay: true
                })
              )
          })
        );
      } else {
        dispatch(
          updateSelectedCustomer({
            customer: client?.value ? (client.value as ClientResponse) : undefined,
            clearDriverPay: false
          })
        );
      }

      if (client?.value) {
        const contactSelected = (client.value as ClientResponse).contacts?.find(
          (contact) => client?.subId === contact.id
        );
        dispatch(updateSelectedContact(contactSelected));
      }
      fetchClients();
    };

    const setNewOrUpdatedClientValue = (clientOption: TwAutocompleteOption): void => {
      onClientSelected(clientOption);
      setFieldValue('customer', clientOption);
    };

    const onContactCreated = (newContact: IContactClient): void => {
      if (!clientToCreateContact) return;

      const contactCustomer = {
        ...clientToCreateContact,
        contacts: [...(clientToCreateContact?.contacts || []), newContact]
      };

      const newAutocompleteOption: TwAutocompleteOption = {
        id: contactCustomer?.id || '',
        subId: newContact.id,
        label: getEndOfCustomerLabel(
          `${contactCustomer?.name} (${newContact.name})`,
          contactCustomer?.currency_preference || ''
        ),
        subLabel: newContact.name,
        value: contactCustomer
      };

      setCustomers((prevCustomers) => [newAutocompleteOption, ...prevCustomers]);
      setNewOrUpdatedClientValue(newAutocompleteOption);

      if (contactCustomer) {
        dispatch(updateSelectedContact(newContact));
      }
    };

    const onCustomerCreated = (newCustomer: ClientResponse): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newCustomer.id,
        subId: newCustomer.id,
        label: getEndOfCustomerLabel(
          `${newCustomer.name} (No contact)`,
          newCustomer.currency_preference || ''
        ),
        subLabel: 'No contact',
        value: newCustomer
      };

      setCustomers((prevCustomers) => [newAutocompleteOption, ...prevCustomers]);
      dispatch(
        addCustomer({
          customer: {
            id: newCustomer.id,
            type: newCustomer.type
          },
          isShipperOrBrokerActive: false
        })
      );

      setNewOrUpdatedClientValue(newAutocompleteOption);
      setClientToCreateContact(newCustomer);
      setIsFirstContact(true);
      setShowContactModal(true);
    };

    const handleRateLabelClick = (): void => {
      // normal rate -> advanced rate
      if (!showAdvancedRates && loadState.rate && loadState.rate > 0) {
        // set default service
        let service = invoiceServicesData?.content.find((service) => service.isDefault);

        // if no default service present use flat rate service
        if (!service) {
          service = invoiceServicesData?.content.find(
            (service) => service.name === LINEHAUL_FLAT_RATE_NAME
          );
        }
        const initialRates = [...initialLoadRates];
        initialRates[0] = {
          ...initialRates[0],
          rate: loadState.rate,
          invoice_service_id: !quickbooksEnabled ? service?.id : undefined,
          qbo_service_id: quickbooksEnabled ? service?.id : undefined,
          qbo_service_type: quickbooksEnabled ? service?.type : undefined,
          service_name: service?.name,
          quantity: 1,
          unit: quickbooksEnabled && service?.isDefault ? '' : service?.unit,
          amount: loadState.rate,
          initial_service_option: service
            ? {
                id: service.id!,
                label: service.name,
                value: service
              }
            : undefined,
          sort_order: 1
        };
        dispatch(updateInitialLoadRates(initialRates));
        dispatch(
          updateLoadRates({
            rates: initialRates
          })
        );
      }

      if (showAdvancedRates) {
        dispatch(updateInitialLoadRates([getEmptyLoadRateServiceRow()]));
        dispatch(setRate(undefined));
        setFieldValue('shipper_rate', undefined);
        setInitialShipperRate(undefined);
      }

      dispatch(toggleAdvancedRates(!showAdvancedRates));
    };

    const handleRatesAdvancedRatesUpdate = (
      rates: RowData[],
      resetDriverPayOverrides: boolean
    ): void => {
      dispatch(updateLoadRates({ rates: rates, resetDriverPayOverrides: resetDriverPayOverrides }));
    };

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter') {
        // I don't use property id here because I test it in console and '' shows. so I compare against outerHTML
        if (e.currentTarget.outerHTML.includes('customer')) {
          setCreateCustomerDialogOpen(true);
        }
      }
    };

    const handleCreateDivision = (response: any): void => {
      const newOption: TwAutocompleteOption = {
        id: response.id,
        label: response.name,
        value: response
      };

      dispatch(updateSelectedDivision(response));
      setFieldValue('division', newOption);
    };

    const handleBrokeredChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(toggleBrokeredLoad(event.target.checked));

      if (!event.target.checked) {
        dispatch(updatePayableAmount(undefined));
      }
    };

    const getEntityToCreate = (): CompaniesEntities | undefined => {
      if (isCreateDivisionOpen) return CompaniesEntities.division;
      if (createCustomerDialogOpen) return CompaniesEntities.customer;
      return undefined;
    };

    const getSuccessActionForCompaniesModal = (response: any): (() => void) | undefined => {
      if (createCustomerDialogOpen) {
        onCustomerCreated(response);
        return;
      }
      if (isCreateDivisionOpen) {
        handleCreateDivision(response);
        return;
      }

      return undefined;
    };

    const handleEditCompaniesClose = (): void => {
      setEditClientOpen(false);

      setIsCreateDivisionOpen(false);
      setCreateCustomerDialogOpen(false);
    };
    const getEndOfCustomerLabel = (
      customerNameLabel: string,
      currency_preference: string
    ): string => {
      if (enableShowCustomerPreferredCurrencyCode && currency_preference) {
        return `${customerNameLabel} - ${currency_preference}`;
      }
      return customerNameLabel;
    };

    const handleEditClientSuccess = async (clientResponse: ClientResponse): Promise<void> => {
      const autocompleteOption: TwAutocompleteOption = {
        id: clientResponse.id,
        subId: clientResponse.id,
        label: `${getEndOfCustomerLabel(
          `${clientResponse.name} (No contact)`,
          clientResponse.currency_preference || ''
        )}`,
        subLabel: 'No contact',
        value: clientResponse
      };
      setNewOrUpdatedClientValue(autocompleteOption);
    };

    const openEditCustomer = (): void => {
      if (!!values.customer) {
        setEditClientOpen(true);
      } else if (!!selectedCustomer) {
        setEditClientOpen(true);
      }
    };

    const handleCloseEditContact = (): void => {
      setEditContactOpen(false);
      setContactToEdit(null);
    };

    const handleEditContactSuccess = (editedContact: IContactClient): void => {
      if (!contactToEdit) return;

      const customer = {
        ...contactToEdit.customer,
        contacts: [...(clientToCreateContact?.contacts || []), editedContact]
      };
      const autocompleteOption = {
        id: editedContact.customer_id || '',
        subId: editedContact.id || '',
        label: getEndOfCustomerLabel(
          `${editedContact.customer?.name} (${editedContact.first_name} ${editedContact.last_name})`,
          customer.currency_preference || ''
        ),
        subLabel: `${editedContact.first_name} ${editedContact.last_name}`,
        value: customer
      };

      const customersNewList = customers.map((item) => {
        if (item.subId === editedContact.id) {
          const newItem = {
            ...item,
            label: getEndOfCustomerLabel(
              editedContact.customer?.name || item.label,
              customer.currency_preference || ''
            ),
            subLabel: `${editedContact.first_name} ${editedContact.last_name}`
          };
          return newItem;
        } else {
          return item;
        }
      });

      setNewOrUpdatedClientValue(autocompleteOption);
      setCustomers(customersNewList);
      handleCloseEditContact();
      setAnchorEl(null);
    };

    const onSendLoadUpdateChange = (): void => {
      const value = !sendLoadUpdateChecked;

      setSendLoadUpdateChecked(value);
      dispatch(setSendLoadUpdate(value));
    };

    const checkCustomerEmailPresent = (): boolean => {
      return !!selectedCustomer && !!(selectedCustomer?.value as ClientResponse).billing_email;
    };

    const checkCustomerContactEmailPresent = (): boolean => {
      return !!providerContact?.email_address;
    };

    const assignLoggedDispatcher = (): void => {
      const loggedDispatcher = dispatchers.find(
        (dispatcher) => (dispatcher.value as DispatcherResponse).twuser_id === userData?.id
      );
      if (loggedDispatcher) {
        setSelectedDispatcher({
          ...selectedDispatcher,
          id: loggedDispatcher.id,
          label: (loggedDispatcher.value as DispatcherResponse).name,
          value: loggedDispatcher.id
        });

        dispatch(updateSelectedDispatcher(loggedDispatcher.id));
      }
    };

    const handleEditDispatcherClose = (): void => {
      setEditDispatcherOpen(false);
      setSelectedDispatcherId('');
    };

    const handleRefetch = (): void => {
      if (pickupDate) {
        getDieselPriceData({
          filters: {
            frequency: Frequency.WEEKLY,
            serieId: selectedRateTableData?.fuel_surcharge_table?.eia_area_id || '',
            numberOfPeriods: 1,
            endDate: pickupDate.toISOString()
          }
        });
      }
    };

    const onDispatcherCreated = (newDispatcher: DispatcherResponse): void => {
      dispatch(updateSelectedDispatcher(newDispatcher.id));

      const newAutocompleteOption: TwAutocompleteOption = {
        id: newDispatcher.id,
        label: `${newDispatcher.first_name} ${newDispatcher.last_name}`,
        value: newDispatcher.id
      };
      setFieldValue('dispatcher', newAutocompleteOption);
    };

    const handleInputRateChange = (
      e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
      if (initialShipperRate) setInitialShipperRate(undefined);
      let value = e.target.value;
      const regex = /^[0-9]*\.?[0-9]{0,2}$/;

      if (value.startsWith('.')) {
        value = `0${value}`;
      }

      if (regex.test(value) && !/[eE]/.test(value)) {
        setFieldValue('shipper_rate', Number(value));
        dispatch(setRate(Number(value)));
        setFieldError('shipper_rate', undefined);
      }

      if (value === '') {
        setFieldValue('shipper_rate', undefined);
        dispatch(setRate(undefined));

        return;
      }
    };

    const handleRateTableSelected = async (rateTableId: string): Promise<void> => {
      if (!showRateTables) return;
      if (rateTableId === '') {
        setFieldValue('rateTableId', undefined);
        dispatch(setRateTableId(undefined));
        const emptyRow = getEmptyLoadRateServiceRow();
        dispatch(updateInitialLoadRates([emptyRow]));
        handleRatesAdvancedRatesUpdate([emptyRow], true);
        setSelectedRateTableData(undefined);
        return;
      }

      const rateTableData = rateTablesData?.content.find(
        (rateTable) => rateTable.id === rateTableId
      );
      if (!rateTableData) return;
      setSelectedRateTableData(rateTableData);

      if (rateTableId !== loadState.rate_table_id) {
        setFieldValue('rateTableId', rateTableId);
        const areQboRates = rateTableData.services.some((rate) => !!rate.qbo_service_id);
        const hideServices =
          (areQboRates && !isQuickbooksEnabledForSelectedDivision) ||
          (!areQboRates && isQuickbooksEnabledForSelectedDivision);
        const data: RowData[] = [...rateTableData.services]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((service) => {
            return {
              id: service.id,
              ui_id: nanoid(),
              rate: service.rate,
              amount: service.rate,
              quantity: service.quantity || undefined,
              unit: service.unit,
              service_name: service.service_name || '',
              invoice_service_id: service?.invoice_service_id || undefined,
              qbo_service_id: service?.qbo_service_id || undefined,
              invoice_service: service.invoice_service,
              sort_order: service.sort_order,
              initial_service_option: {
                id: isQuickbooksEnabledForSelectedDivision
                  ? service.qbo_service_id || ''
                  : service.invoice_service_id || '',
                label: hideServices ? '' : service.service_name || '',
                value: {
                  id: isQuickbooksEnabledForSelectedDivision
                    ? service.qbo_service_id || ''
                    : service.invoice_service_id || '',
                  name: hideServices ? '' : service.service_name || '',
                  default_rate: 0,
                  unit: service.unit
                } as InvoiceService
              }
            };
          });

        if (rateTableData.fuel_surcharge_table) {
          let newPrice = undefined;
          let periodDate = rateTableData.fuel_surcharge_table.period_date;
          const pickupDateToUse = pickupDate ? pickupDate : new Date();

          if (rateTableData.fuel_surcharge_table.fuel_price_type === FuelPriceType.EIA) {
            // Fetch diesel price data based on the pickup date
            setIsDieselPriceDataFetching(true);
            setOpenLoadingEiaPriceModal(true);
            try {
              const dieselPriceData = await getDieselPriceData({
                filters: {
                  frequency: Frequency.WEEKLY,
                  serieId: rateTableData.fuel_surcharge_table.eia_area_id || '',
                  numberOfPeriods: 1,
                  endDate: pickupDateToUse.toISOString()
                }
              }).unwrap();
              if (dieselPriceData.length > 0) {
                newPrice = dieselPriceData[0].value;
                periodDate = new Date(dieselPriceData[0].period);
              } else {
                newPrice = null;
              }
            } catch (error) {
              setIsDieselPriceDataError(true);
            } finally {
              setIsDieselPriceDataFetching(false);
              setOpenLoadingEiaPriceModal(false);
            }
          }
          const { rate, price, hasError, message } = FuelSurchargeTableUtils.determineFuelPrice(
            rateTableData.fuel_surcharge_table,
            pickupDateToUse,
            newPrice
          );
          let tooltip = undefined;
          if (hasError) {
            addNotification({
              variant: 'warning',
              timeToShow: 5000,
              message: message || 'Fuel surcharge table error'
            });
          } else {
            tooltip = FuelSurchargeTableUtils.generateFuelSurchargeTableTooltip({
              rate: rate,
              price: price,
              unit: rateTableData.fuel_surcharge_table.unit,
              fuelPriceType: rateTableData.fuel_surcharge_table.fuel_price_type,
              eiaAreaName: rateTableData.fuel_surcharge_table.eia_area_name || '',
              periodDate: periodDate || undefined,
              loadPickupDate: pickupDateToUse.toLocaleDateString()
            });
          }
          const fuelService = rateTableData.fuel_surcharge_table?.qbo_service_id;
          const qboConnected = !!accountingSettings?.division_qbo_credentials?.id;
          const service = qboConnected
            ? invoiceServicesData?.content.find((service) => service.id === fuelService)
            : rateTableData.fuel_surcharge_table.invoice_service;
          const defaultServiceId = qboConnected
            ? accountingSettings?.default_qbo_invoice_service_id || ''
            : accountingSettings?.default_invoice_service_id || '';

          const defaultServiceAmount = InvoiceUtils.calculateDefaultServiceAmount({
            rows: data,
            defaultServiceId
          });

          // Calculate quantity based on unit and invoice source
          // If unit is PER_MILE, use overwritten mileage if present, otherwise use 0
          // If unit is PERCENTAGE, use default service amount (sum of all default services in the rate table)
          // service is the service of fuel surcharge table in order to know if it's a default service or not
          const calculatedQuantity = InvoiceUtils.getQuantityBasedOnUnitAndInvoiceSource({
            service: service || undefined,
            totalLoadedMiles: rateTableData.overwritten_mileage || 0,
            amountOfDefaultService: defaultServiceAmount || 0,
            isQboEnabled: isQuickbooksEnabledForSelectedDivision || false,
            unit: rateTableData.fuel_surcharge_table.unit,
            overwrittenMileage: rateTableData.overwritten_mileage || undefined
          });

          const calculatedAmount = InvoiceUtils.calculateRowAmount({
            rate: rate || 0,
            quantity: calculatedQuantity || 0,
            unit: rateTableData.fuel_surcharge_table.unit,
            isQboService: isQuickbooksEnabledForSelectedDivision || false
          });

          data.push({
            id: rateTableData.fuel_surcharge_table.id,
            ui_id: nanoid(),
            rate: rate,
            amount: calculatedAmount,
            quantity: calculatedQuantity || 0,
            unit: rateTableData.fuel_surcharge_table.unit,
            service_name: service?.name || '',
            invoice_service_id: rateTableData.fuel_surcharge_table.invoice_service_id || undefined,
            qbo_service_id: rateTableData.fuel_surcharge_table.qbo_service_id || undefined,
            sort_order: data.length + 1,
            has_rate_error: hasError,
            tooltip: tooltip,
            initial_service_option: {
              id: isQuickbooksEnabledForSelectedDivision
                ? rateTableData.fuel_surcharge_table.qbo_service_id || ''
                : rateTableData.fuel_surcharge_table.invoice_service_id || '',
              label: hideServices ? '' : service?.name || '',
              value: {
                id: isQuickbooksEnabledForSelectedDivision
                  ? rateTableData.fuel_surcharge_table.qbo_service_id || ''
                  : rateTableData.fuel_surcharge_table.invoice_service_id || '',
                name: hideServices
                  ? ''
                  : rateTableData.fuel_surcharge_table.invoice_service?.name || '',
                default_rate: 0,
                unit: rateTableData.fuel_surcharge_table.unit
              } as InvoiceService
            },
            is_fuelSurcharge_row: true
          });
        }

        dispatch(toggleAdvancedRates(true));
        dispatch(setRateTableId(rateTableId || undefined));
        dispatch(updateInitialLoadRates(data));
        handleRatesAdvancedRatesUpdate(data, true);
      }
    };

    const customerMenuOptions = [
      {
        label: 'Create new customer',
        action: (): void => {
          setCreateCustomerDialogOpen(true);
        },
        disabled: false
      },
      {
        label: 'Edit customer',
        action: (): void => openEditCustomer(),
        disabled: !selectedCustomer
      },
      {
        label: 'Create contact',
        action: (): void => {
          setClientToCreateContact(
            selectedCustomer?.value ? (selectedCustomer.value as ClientResponse) : null
          );
          setIsFirstContact(false);
          setShowContactModal(true);
        },
        disabled: !selectedCustomer
      },
      {
        label: 'Edit contact',
        action: (): void => {
          if (selectedCustomer && selectedCustomer.value && selectedCustomer.subId) {
            setEditContactOpen(true);
            setContactToEdit({
              customer: selectedCustomer.value as ClientResponse,
              id: selectedCustomer.subId
            });
          }
        },
        disabled:
          !selectedCustomer || (selectedCustomer && selectedCustomer.subLabel === 'No contact')
      }
    ];
    // #endregion

    // memoized values
    const disableSendLoadUpdate = useMemo<boolean>(() => {
      const isCustomerMissing = !selectedCustomer && !values.customer;
      if (isCustomerMissing) return true;

      return !(checkCustomerEmailPresent() || checkCustomerContactEmailPresent());
    }, [selectedCustomer, values.customer, orderNotificationSavedSettings]);

    // #region Effects

    useEffect(() => {
      if (divisionId) {
        setSelectedDivisionId(divisionId);
      } else if (userData?.defaultDivision.id) {
        setSelectedDivisionId(userData.defaultDivision.id);
      }
    }, [divisionId, userData?.defaultDivision.id]);

    useEffect(() => {
      // if defined and no load rates set and showAdvancedRates is false
      if (defaultQboServiceId && !loadState.rate && !showAdvancedRates) {
        // set default service
        const defaultService = invoiceServicesData?.content.find((service) => service.isDefault);
        // if no default, return, we shoulnt be here
        if (!defaultService) return;
        const initialRates = [...initialLoadRates];
        initialRates[0] = {
          ...initialRates[0],
          rate: loadState.rate,
          invoice_service_id: !isQuickbooksEnabledForSelectedDivision
            ? defaultService?.id
            : undefined,
          qbo_service_id: isQuickbooksEnabledForSelectedDivision ? defaultService?.id : undefined,
          qbo_service_type: isQuickbooksEnabledForSelectedDivision
            ? defaultService?.type
            : undefined,
          service_name: defaultService?.name,
          quantity: 1,
          unit:
            isQuickbooksEnabledForSelectedDivision && defaultService?.isDefault
              ? ''
              : defaultService?.unit,
          amount: loadState.rate,
          initial_service_option: defaultService
            ? {
                id: defaultService.id!,
                label: defaultService.name,
                value: defaultService
              }
            : undefined,
          sort_order: 1
        };

        dispatch(updateInitialLoadRates(initialRates));
        dispatch(
          updateLoadRates({
            rates: initialRates
          })
        );
      }
    }, [defaultQboServiceId]);

    useEffect(() => {
      if (quickbooksEnabled && !invoiceServicesError) {
        const defaultService =
          invoiceServicesData?.content.find((item) => (item as InvoiceService)?.isDefault) || null;

        if (defaultService) {
          setDefaultQboServiceId(defaultService.id);
        } else {
          setDefaultQboServiceId(null);
        }
      }
    }, [invoiceServicesData, quickbooksEnabled, invoiceServicesError]);

    useEffect(() => {
      if (!!accountingSettings?.division_qbo_credentials?.id) {
        dispatch(updateQuickbooksEnabled(true));
        setIsQuickbooksEnabledForSelectedDivision(true);
      } else {
        dispatch(updateQuickbooksEnabled(false));
        setIsQuickbooksEnabledForSelectedDivision(false);
      }
    }, [accountingSettings?.division_qbo_credentials?.id]);

    useEffect(() => {
      if ((loadState.order_id || isImport) && !initialShipperRate) {
        setInitialShipperRate(loadState.rate);
        setFieldValue('shipper_rate', loadState.rate);
      }
    }, [loadState.order_id, loadState.rate, isImport]);

    useEffect(() => {
      if (
        (loadState.order_id || isImport) &&
        !initialShipperLoadNumber &&
        !loadState.duplicate_load
      ) {
        setInitialShipperLoadNumber(loadState.customer_order_number);
        setFieldValue('customerOrderNumber', loadState.customer_order_number);
      }
    }, [loadState.order_id, isImport, loadState.customer_order_number]);

    useEffect(() => {
      if (loadState.payable_amount && !payableAmount && loadState.is_brokered_order) {
        setPayableAmount(loadState.payable_amount);
      }
    }, [loadState.is_brokered_order, loadState.payable_amount]);

    useEffect(() => {
      if (loadState.instructions && Object.keys(values).length === 0 && !loadState.duplicate_load) {
        setFieldValue('instructions', loadState.instructions);
      }
    }, [loadState.instructions]);

    useEffect(() => {
      const customers = [...twClients];
      const suggestedContacts: TwAutocompleteOption[] = [];

      customers.forEach((customer) => {
        suggestedContacts.push({
          id: customer.id,
          subId: customer.id,
          label: getEndOfCustomerLabel(customer.name, customer.currency_preference || ''),
          subLabel: 'No contact',
          value: customer
        });

        customer.contacts?.forEach((contact) => {
          suggestedContacts.push({
            id: customer.id,
            subId: contact.id, // For each contact TwAutocompletOption subId is the contact id.
            label: getEndOfCustomerLabel(customer.name, customer.currency_preference || ''),
            subLabel: contact.name, // For each contact TwAutocompletOption subLabel is the contact name.
            value: customer
          });
        });
      });
      console.log('suggestedContacts', suggestedContacts);

      setCustomers(suggestedContacts);
    }, [twClients, enableShowCustomerPreferredCurrencyCode]);

    useEffect(() => {
      const required = LoadUtils.getRequiredFields(loadState.status, createLoadConfig);

      setRequiredFields(required);
    }, [loadState.status]);

    useEffect(() => {
      setFieldValue('rateOptionalEnable', rateOptionalEnable);
    }, [rateOptionalEnable]);

    useEffect(() => {
      if (!selectedDivision && loadState.provider_division) {
        setSelectedDivision({
          id: loadState.provider_division.id,
          label: loadState.provider_division.name,
          value: loadState.provider_division
        });
      } else if (!selectedDivision && divisionsData.content.length > 0 && !isEditLoadOpen) {
        // Pre-select division as the division with the division_id that is the same as the tenant's default division
        const defaultDivision = divisionsData.content.find(
          (division) => division.id === userData?.defaultDivision?.id
        );
        setSelectedDivision(defaultDivision);
        dispatch(updateSelectedDivision(defaultDivision?.value as IDivisionsResponse));
      }
    }, [loadState.provider_division, isEditLoadOpen, divisionsData.content.map((d) => d.id)]);

    useEffect(() => {
      if (isEditLoadOpen && anchorId && !loading) {
        if (anchorId === 'load-build-rate-section') {
          scrollToView(rateRef, 'center');
          setTimeout(() => dispatch(setAnchorId(null)), 500);
        }
      }
    }, [anchorId, isEditLoadOpen, loading]);

    useEffect(() => {
      //  fetchClients();
      setSendLoadUpdateChecked(sendLoadUpdate);
    }, []);

    useEffect(() => {
      // If request returns savedOrderNotificationSettings, we update the config in state
      // so we have data in case the user does not edit settings
      if (savedOrderNotificationSettings && !orderNotificationSavedSettings) {
        dispatch(updateLoadNotificationSettings(savedOrderNotificationSettings));
      }
    }, [savedOrderNotificationSettings]);

    useEffect(() => {
      if (sendLoadUpdateChecked) {
        if (orderNotificationSavedSettings) {
          dispatch(updateLoadNotificationSettings(orderNotificationSavedSettings));
        }
      }
    }, [sendLoadUpdateChecked]);

    useEffect(() => {
      // If no recipients are selected to receive the load update,
      // uncheck the send load update checkbox
      if (
        !!orderNotificationSavedSettings &&
        orderNotificationSavedSettings?.recipients?.additional_emails?.length === 0 &&
        !orderNotificationSavedSettings.recipients.customer_billing_email &&
        !orderNotificationSavedSettings.recipients.customer_contact_email &&
        !orderNotificationSavedSettings.recipients.user_dispatching_order
      ) {
        setSendLoadUpdateChecked(false);
        dispatch(setSendLoadUpdate(false));
      } else if (sendLoadUpdate) {
        setSendLoadUpdateChecked(true);
      }
    }, [orderNotificationSavedSettings, sendLoadUpdate]);

    useEffect(() => {
      if (isEditLoadOpen) {
        setBlockFetchCustomers(true);

        setTimeout(() => {
          setBlockFetchCustomers(false);
        }, 500);
      }
    }, [isEditLoadOpen]);

    useEffect(() => {
      if (!selectedCustomer && providerCustomer) {
        const customerLabelFirstPart = providerContact
          ? `${providerCustomer.name} (${providerContact.name})`
          : `${providerCustomer.name} (No contact)`;
        setSelectedCustomer({
          id: providerCustomer.id,
          subId: providerCustomer.id,
          label: getEndOfCustomerLabel(
            customerLabelFirstPart,
            providerCustomer.currency_preference || ''
          ),
          subLabel: providerContact?.name || 'No contact',
          value: providerCustomer
        });
      }
      if (selectedCustomer) {
        setFieldValue('customer', selectedCustomer);

        const customer = selectedCustomer.value as ClientResponse;
        if (customer.preferences) {
          const notify =
            !!customer.preferences.order_notification_settings.notify_customer_when_status_changes;

          dispatch(
            updateLoadNotificationSettings(customer.preferences.order_notification_settings)
          );
          setSendLoadUpdateChecked(notify);
          dispatch(setSendLoadUpdate(notify));
        } else {
          setSendLoadUpdateChecked(false);
          dispatch(setSendLoadUpdate(false));
          dispatch(updateLoadNotificationSettings(savedOrderNotificationSettings));
        }
      }
    }, [providerCustomer, enableShowCustomerPreferredCurrencyCode]);

    useEffect(() => {
      if (dispatcherEnable) {
        const newDispatchers = dispatchersData.content.map((dispatcher) => ({
          id: dispatcher.id,
          label: `${dispatcher.first_name} ${dispatcher.last_name}`,
          value: dispatcher
        }));
        setDispatchers(newDispatchers);
      }
    }, [JSON.stringify(dispatchersData.content.map((d) => d.id)), dispatcherEnable, loadType]);

    useEffect(() => {
      if (dispatchers.length > 0 && dispatcherEnable) {
        if (loadState.duplicate_load && firstRender) {
          setFirstRender(false);
          assignLoggedDispatcher();
        }

        if (
          dispatcherEnable &&
          !isEditLoadOpen &&
          !loadState.duplicate_load &&
          !loadState.selected_dispatcher &&
          firstRender
        ) {
          setFirstRender(false);
          assignLoggedDispatcher();
        }

        if (loadState.selected_dispatcher && firstRender) {
          const selectedDispatcher = dispatchers.find(
            (dispatcher) => dispatcher.id === loadState.selected_dispatcher
          );
          setFirstRender(false);
          setSelectedDispatcher(selectedDispatcher);
        }
      }
    }, [
      loadState.selected_dispatcher,
      dispatchers,
      selectedDispatcher,
      dispatcherEnable,
      loadType
    ]);

    useEffect(() => {
      if (trips.length > 0) {
        const trip = trips[0];
        const pickup = trip?.provider_stops ? trip?.provider_stops[0] : null;

        if (pickup && pickup.provider_date && isValidDate(pickup.provider_date)) {
          // if pickup date is different from the current pickup date set it
          if (new Date(pickup.provider_date).getTime() !== pickupDate?.getTime()) {
            setPickupDate(new Date(pickup.provider_date));
          }
        } else {
          // if current pickup date is set and there is no pickup date in the trip, reset it
          if (pickupDate) {
            setPickupDate(null);
          }
        }
      }
    }, [JSON.stringify(trips)]);

    useEffect(() => {
      if (showRateTables) {
        if (loadState.rate_table_id && loadState.is_rate_table_deleted === true) {
          dispatch(setRateTableId(undefined));
          addNotification({
            variant: 'warning',
            timeToShow: 5000,
            message: 'The rate table associated with this order is deleted.'
          });
        }

        if (loadState.rate_table_id && loadState.is_rate_table_active === false) {
          dispatch(setRateTableId(undefined));
          addNotification({
            variant: 'warning',
            timeToShow: 5000,
            message: 'The rate table associated with this order is expired.'
          });
        }
      }
    }, [loadState.is_rate_table_deleted, loadState.is_rate_table_active, showRateTables]);

    useEffect(() => {
      if (showRateTables) {
        if (rateTablesData?.content && loadState.rate_table_id) {
          const newRateTables = rateTablesData?.content;
          const selectedRateTable = newRateTables?.find(
            (rateTable) => rateTable.id === loadState.rate_table_id
          );

          if (!selectedRateTable) {
            dispatch(setRateTableId(undefined));
            addNotification({
              variant: 'warning',
              timeToShow: 5000,
              message:
                'Rate table data has been updated, the selected rate table is no longer available.'
            });
          }
        }
      }
    }, [rateTablesData?.content, showRateTables]);

    useEffect(() => {
      (async (): Promise<void> => {
        if (
          showRateTables &&
          (isEditLoadOpen ||
            isCreateOrderOpen ||
            isCreateCustomerOrderOpen ||
            isCreateLoadFromFileOpen ||
            isParentModalOpen)
        ) {
          if (selectedRateTableData?.fuel_surcharge_table) {
            const pickupDateToUse = pickupDate ? pickupDate : new Date();
            const findOrderRateRowAsFuelSurchargeRow = loadState.order_rates.find(
              (row) => row.is_fuelSurcharge_row
            );
            if (findOrderRateRowAsFuelSurchargeRow) {
              const currentRowServiceName = findOrderRateRowAsFuelSurchargeRow.service_name;
              const rateClearedMessage = `The rate for ${currentRowServiceName} has been cleared because the updated pickup date is outside the valid date range. Please update the date range or enter a new rate manually.`;
              const rateUpdatedMessage = `The rate for ${currentRowServiceName} has been updated to match the new pickup date range. Please review the rate to ensure accuracy.`;
              const findIndex = loadState.order_rates.findIndex((row) => row.is_fuelSurcharge_row);
              let newPrice = undefined;
              let periodDate = selectedRateTableData.fuel_surcharge_table.period_date;
              // get diesel price data
              if (
                selectedRateTableData.fuel_surcharge_table.fuel_price_type === FuelPriceType.EIA
              ) {
                setIsDieselPriceDataFetching(true);
                setOpenLoadingEiaPriceModal(true);
                try {
                  const dieselPriceData = await getDieselPriceData({
                    filters: {
                      frequency: Frequency.WEEKLY,
                      serieId: selectedRateTableData.fuel_surcharge_table.eia_area_id || '',
                      numberOfPeriods: 1,
                      endDate: pickupDateToUse.toISOString()
                    }
                  }).unwrap();
                  if (dieselPriceData.length > 0) {
                    newPrice = dieselPriceData[0].value;
                    periodDate = new Date(dieselPriceData[0].period);
                  } else {
                    newPrice = null;
                  }
                } catch (error) {
                  setIsDieselPriceDataError(true);
                } finally {
                  setIsDieselPriceDataFetching(false);
                  setOpenLoadingEiaPriceModal(false);
                }
              }
              const { rate, price, hasError, message } = FuelSurchargeTableUtils.determineFuelPrice(
                selectedRateTableData.fuel_surcharge_table,
                pickupDateToUse,
                newPrice
              );
              const newRateRow: SliceOrderRate = {
                id: findOrderRateRowAsFuelSurchargeRow.id,
                ui_id: findOrderRateRowAsFuelSurchargeRow.ui_id,
                rate: hasError ? undefined : rate,
                amount: hasError ? undefined : price,
                quantity: findOrderRateRowAsFuelSurchargeRow.quantity,
                unit: findOrderRateRowAsFuelSurchargeRow.unit,
                service_name: findOrderRateRowAsFuelSurchargeRow.service_name,
                invoice_service_id: findOrderRateRowAsFuelSurchargeRow.invoice_service_id,
                qbo_service_id: findOrderRateRowAsFuelSurchargeRow.qbo_service_id,
                sort_order: findOrderRateRowAsFuelSurchargeRow.sort_order,
                is_fuelSurcharge_row: true,
                has_rate_error: hasError,
                tooltip: message,
                initial_service_option: findOrderRateRowAsFuelSurchargeRow.initial_service_option
              };
              if (hasError) {
                addNotification({
                  variant: 'warning',
                  timeToShow: 5000,
                  message: findOrderRateRowAsFuelSurchargeRow.rate
                    ? rateClearedMessage
                    : message || 'Fuel surcharge table error'
                });
              } else {
                newRateRow.tooltip = FuelSurchargeTableUtils.generateFuelSurchargeTableTooltip({
                  rate: rate,
                  price: price,
                  unit: selectedRateTableData.fuel_surcharge_table.unit,
                  fuelPriceType: selectedRateTableData.fuel_surcharge_table.fuel_price_type,
                  eiaAreaName: selectedRateTableData.fuel_surcharge_table.eia_area_name || '',
                  periodDate: periodDate || undefined,
                  loadPickupDate: pickupDateToUse.toLocaleDateString()
                });
                if (findOrderRateRowAsFuelSurchargeRow.rate !== rate) {
                  addNotification({
                    variant: 'success',
                    timeToShow: 5000,
                    message: rateUpdatedMessage
                  });
                }
              }
              const newOrderRates = [...loadState.order_rates];
              newOrderRates[findIndex] = newRateRow;
              dispatch(updateInitialLoadRates(newOrderRates));
              handleRatesAdvancedRatesUpdate(newOrderRates, false);
            }
          }
        }
      })();
    }, [pickupDate]);

    return (
      <>
        <Box
          ref={ref}
          component="div"
          className={`${
            (isImport && !fullScreenImport) || matchesMobileOrTablet
              ? 'route-info__main-import'
              : 'route-info__main'
          } ${className || ''}`}
        >
          <span
            className={
              (isImport && !fullScreenImport) || matchesMobileOrTablet
                ? 'route-info__title-left'
                : 'route-info__title'
            }
          >
            Order Info
          </span>
          <span
            className={
              isImport || matchesMobileOrTablet
                ? 'route-info__sub-title-left'
                : 'route-info__sub-title'
            }
          >
            Add your order information
          </span>

          <Box sx={dataContainerSx}>
            {enableLtl && (
              <>
                <Typography component="h5" className="route-info__info-title">
                  Booking type
                </Typography>
                <Box className="route-info__booking-type">
                  <TwRadioButton
                    value={BOOKING_TYPE.FTL}
                    name="ftl-order"
                    label="FTL order"
                    checked={loadState.booking_type === BOOKING_TYPE.FTL}
                    onInputChange={(): void => {
                      dispatch(setBookingType(BOOKING_TYPE.FTL));
                    }}
                  />

                  <TwRadioButton
                    value={BOOKING_TYPE.LTL}
                    name="ltl-order"
                    label="LTL order"
                    checked={loadState.booking_type === BOOKING_TYPE.LTL}
                    onInputChange={(): void => {
                      dispatch(setBookingType(BOOKING_TYPE.LTL));
                    }}
                  />
                </Box>
              </>
            )}
            {brokeredEnabled && !createOrder && !enableLtl && (
              <FormControlLabel
                className="route-info__brokered-switch"
                value="start"
                control={
                  <TwSwitch checked={loadState.is_brokered_order} onChange={handleBrokeredChange} />
                }
                label={
                  <>
                    <span className="route-info__brokered-label">Brokered order</span>
                    <TwTooltip title="Enable if freight is brokered; payable amount field will be added">
                      <InfoIcon />
                    </TwTooltip>
                  </>
                }
                labelPlacement="start"
              />
            )}

            <Grid container spacing={2} sx={{ marginBottom: '1rem' }}>
              {divisionsEnabled && (
                <Grid item xs={12} md={isImport && !fullScreenImport ? 12 : 6}>
                  {divisionEditable ? (
                    <TwAutocomplete
                      id="division"
                      name="division"
                      label="Division"
                      optionList={divisionsData.content}
                      value={values.division || selectedDivision || ''}
                      onChange={(_, newValue: TwAutocompleteOption | null): void => {
                        dispatch(updateSelectedDivision(newValue?.value as IDivisionsResponse));
                        setFieldValue('division', newValue);
                        setSelectedDivisionId(newValue?.id as string);

                        if (newValue === null) {
                          setSelectedDivision(null);
                          setSelectedDivisionId(undefined);
                        }
                      }}
                      listAction={{
                        label: 'New division',
                        action: (): void => {
                          setIsCreateDivisionOpen(true);
                        }
                      }}
                      disableNoOption
                      disableClearable
                      error={'division' in errors}
                    />
                  ) : (
                    <DisabledBox value={selectedDivision?.label || ''} />
                  )}
                </Grid>
              )}
              {enableLtl && dispatcherEnable && (
                <Grid item xs={12} md={6} className="contact-details__dispatcher-edit">
                  <TwAutocomplete
                    id="dispatcher"
                    name="dispatcher"
                    label="Dispatcher"
                    optionList={dispatchers}
                    value={selectedDispatcher ? selectedDispatcher : null}
                    onChange={(_, newValue: TwAutocompleteOption | null): void => {
                      dispatch(updateSelectedDispatcher(newValue?.id));
                      setSelectedDispatcher(newValue);
                      setFieldValue('dispatcher', newValue);
                    }}
                    listAction={{
                      label: 'New dispatcher',
                      action: (): void => setCreateDispatcherDialogOpen(true)
                    }}
                    disableNoOption={true}
                    enableClearIcon
                    onKeyDown={onKeyDownHandler}
                    sx={{ marginBottom: '16px' }}
                  />
                  {(selectedDispatcher || values.dispatcher) && (
                    <TwTooltip
                      title={
                        'Editing selected dispatcher will impact all loads where the dispatcher is used'
                      }
                      placement="top"
                    >
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => {
                          if (!!values.dispatcher) {
                            setSelectedDispatcherId(values.dispatcher.id);
                            setEditDispatcherOpen(true);
                          } else if (!!selectedDispatcher) {
                            setSelectedDispatcherId(selectedDispatcher.id);
                            setEditDispatcherOpen(true);
                          }
                        }}
                      />
                    </TwTooltip>
                  )}
                </Grid>
              )}
            </Grid>
            {loadType === LOAD_TYPE.SEGMENT && dispatcherEnable && !enableLtl && (
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={isImport && !fullScreenImport ? 12 : 6}
                  className="contact-details__dispatcher-edit"
                >
                  <TwAutocomplete
                    id="dispatcher"
                    name="dispatcher"
                    label="Dispatcher"
                    optionList={dispatchers}
                    value={selectedDispatcher ? selectedDispatcher : null}
                    onChange={(_, newValue: TwAutocompleteOption | null): void => {
                      dispatch(updateSelectedDispatcher(newValue?.id));
                      setSelectedDispatcher(newValue);
                      setFieldValue('dispatcher', newValue);
                    }}
                    listAction={{
                      label: 'New dispatcher',
                      action: (): void => setCreateDispatcherDialogOpen(true)
                    }}
                    disableNoOption={true}
                    enableClearIcon
                    onKeyDown={onKeyDownHandler}
                  />
                  {(selectedDispatcher || values.dispatcher) && (
                    <TwTooltip
                      title={
                        'Editing selected dispatcher will impact all loads where the dispatcher is used'
                      }
                      placement="top"
                    >
                      <EditIcon
                        className="contact-details__edit-icon"
                        onClick={(): void => {
                          if (!!values.dispatcher) {
                            setSelectedDispatcherId(values.dispatcher.id);
                            setEditDispatcherOpen(true);
                          } else if (!!selectedDispatcher) {
                            setSelectedDispatcherId(selectedDispatcher.id);
                            setEditDispatcherOpen(true);
                          }
                        }}
                      />
                    </TwTooltip>
                  )}
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2}>
              {!createOrder && (
                <Grid item xs={12} md={isImport && !fullScreenImport ? 12 : 6}>
                  <div className="tid-contact-details__customer contact-details__customer">
                    <div className="contact-details__customer-edit">
                      <TwAutocomplete
                        id="customer"
                        name="customer"
                        label="Customer *"
                        placeholder="Search by customer name…"
                        optionList={customers}
                        renderOption={(props, option): React.ReactNode => {
                          return <RenderOption props={props} option={option} />;
                        }}
                        value={selectedCustomer ? selectedCustomer : null}
                        filterOptions={(x: any): any => x}
                        onInputChange={(_, value): void => {
                          const hasSelectedValueChanged = selectedCustomer?.label !== value;
                          hasSelectedValueChanged &&
                            !blockFetchCustomers &&
                            fetchClients(value ?? undefined);
                        }}
                        onChange={(_, newValue: TwAutocompleteOption | null): void => {
                          const newClient = newValue
                            ? ({ ...newValue } as TwAutocompleteOption)
                            : null;
                          const customer = newValue?.value as ClientResponse;

                          newClient
                            ? (newClient.label = `${getEndOfCustomerLabel(
                                `${customer.name} (${newValue?.subLabel})`,
                                customer.currency_preference || ''
                              )}`)
                            : null;
                          // if rate tables are enabled, clear driver pay overrides on customer change/remove
                          onClientSelected(newClient, rateTablesEnabled ? true : false);
                          setFieldValue('customer', newClient);
                          if (!newClient && rateTablesEnabled) {
                            dispatch(updateInitialLoadRates([getEmptyLoadRateServiceRow()]));
                            dispatch(setRateTableId(undefined));
                          }
                        }}
                        listAction={{
                          label: 'New customer',
                          action: (): void => {
                            setCreateCustomerDialogOpen(true);
                          }
                        }}
                        disableNoOption={true}
                        enableClearIcon
                        onKeyDown={onKeyDownHandler}
                        className="contact-details__no-right-border"
                        error={!!errors.customer}
                      />
                      <IconButton
                        disableRipple={true}
                        className="contact-details__menu-icon"
                        onClick={(event: React.MouseEvent<HTMLElement>): void =>
                          setAnchorEl(event.currentTarget)
                        }
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <TwCustomerMenu
                        anchor={anchorEl}
                        close={(): void => setAnchorEl(null)}
                        menuOptions={customerMenuOptions}
                      />
                    </div>

                    <Box className="contact-details__notify-customer-wrapper">
                      <TwTooltip
                        title={
                          values.customer && disableSendLoadUpdate
                            ? 'An email address is needed to enable automatic updates. Please add an email address to the customer or customer contact.'
                            : ''
                        }
                        placement="top"
                      >
                        <TwCheckbox
                          label="Notify customer when status changes"
                          name="customer_send_load_update"
                          checked={!!selectedCustomer && sendLoadUpdateChecked}
                          disabled={disableSendLoadUpdate}
                          onInputChange={onSendLoadUpdateChange}
                        />
                      </TwTooltip>
                      {!!selectedCustomer && sendLoadUpdateChecked ? (
                        <span
                          className="contact-details__action-label"
                          onClick={(): void => {
                            // Open edit load notification settings dialog
                            setEditLoadNotificationSettingsOpen(true);
                          }}
                        >
                          Edit order notification settings
                        </span>
                      ) : (
                        <span className="contact-details__action-label--disabled">
                          Edit order notification settings
                        </span>
                      )}
                    </Box>
                  </div>
                </Grid>
              )}

              <Grid item xs={12} md={isImport ? 12 : 6}>
                <TwField
                  id="customerOrderNumber"
                  name="customerOrderNumber"
                  label="Customer order #"
                  inputType={TwInputTypes.text}
                  value={values.customerOrderNumber || initialShipperLoadNumber || ''}
                  onInputBlur={(e): void => {
                    dispatch(setShipperLoadNumber(e.target.value));
                  }}
                  onInputChange={(e): void => {
                    if (initialShipperLoadNumber) setInitialShipperLoadNumber(undefined);
                    setFieldValue('customerOrderNumber', e.target.value);
                  }}
                  maxRows={1}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TwField
                  id="instructions"
                  name="instructions"
                  label="Order notes"
                  inputType={TwInputTypes.textareaAuto}
                  value={values.instructions}
                  onInputBlur={(e): void => {
                    dispatch(updateInstructions(e.target.value));
                  }}
                  onInputChange={(e): void => {
                    setFieldValue('instructions', e.target.value);
                  }}
                />
              </Grid>
            </Grid>

            {!createOrder && (
              <>
                <Box
                  display="flex"
                  ref={rateRef}
                  justifyContent="space-between"
                  id="load-build-rate-section"
                >
                  <Typography component="h5" className="route-info__info-title">
                    Rate information
                  </Typography>

                  {/* Don't show toggle if QBO and no default service */}
                  {(!quickbooksEnabled || defaultQboServiceId) && (
                    <Box
                      component="label"
                      className="route-info__action-label"
                      onClick={(): void => {
                        if (anyDriverPayExists) {
                          dispatch(
                            updateDriverPayConfirmationModal({
                              open: true,
                              action: handleRateLabelClick
                            })
                          );
                        } else {
                          handleRateLabelClick();
                        }
                      }}
                    >
                      {`Show ${showAdvancedRates ? 'simple' : 'advanced'} rate${
                        showAdvancedRates ? '' : 's'
                      }`}
                    </Box>
                  )}
                </Box>

                {showAdvancedRates || (quickbooksEnabled && !defaultQboServiceId) ? (
                  <>
                    {showRateTables && (
                      <Box className="route-info__rate-table " sx={DivRateTableSx}>
                        {!isRateTableLoading &&
                        rateTablesData?.content &&
                        rateTablesData?.content.length ? (
                          <TwRateTableSelector
                            data={rateTablesData?.content || []}
                            customerId={selectedCustomer?.id}
                            onRateTableSelected={(id): void => {
                              if (anyDriverPayExists) {
                                dispatch(
                                  updateDriverPayConfirmationModal({
                                    open: true,
                                    action: () => handleRateTableSelected(id)
                                  })
                                );
                              } else {
                                handleRateTableSelected(id);
                              }
                            }}
                            value={loadState.rate_table_id}
                          />
                        ) : (
                          <TwEmptyRateTableSelector customerId={selectedCustomer?.id} />
                        )}
                      </Box>
                    )}
                    <TwAdvancedRates
                      initialData={initialLoadRates}
                      onRatesUpdate={handleRatesAdvancedRatesUpdate}
                      // Advanced rates mileage is independent from Loaded miles (estimated_mileage)
                      totalLoadedMiles={
                        advancedRatesMiles ? NumberUtils.roundNumber(advancedRatesMiles, 2) : 0
                      }
                      isImportLoad={!!isImport}
                      selectedDivisionId={loadState.selected_division || null}
                    />
                  </>
                ) : (
                  <Stack spacing={1} className="route-info__rate-stack">
                    <TwField
                      name="shipper-rate"
                      label={`Linehaul rate ${
                        requiredFields.includes('rate') || !rateOptionalEnable ? '*' : ''
                      }`}
                      className="w-108 route-info__rate "
                      inputType={TwInputTypes.number}
                      value={values.shipper_rate || initialShipperRate}
                      autoComplete="off"
                      onInputChange={(e): void => {
                        if (anyDriverPayExists) {
                          e.stopPropagation();
                          dispatch(
                            updateDriverPayConfirmationModal({
                              open: true,
                              action: () => handleInputRateChange(e)
                            })
                          );
                        } else {
                          handleInputRateChange(e);
                        }
                      }}
                      startAdorment={<InputAdornment position="start">$</InputAdornment>}
                      error={'shipper_rate' in errors}
                    />
                    {showRateTables && (
                      <Box className="route-info__rate-table " sx={DivRateTableSx}>
                        {!isRateTableLoading &&
                        rateTablesData?.content &&
                        rateTablesData?.content.length ? (
                          <TwRateTableSelector
                            data={rateTablesData?.content || []}
                            customerId={selectedCustomer?.id}
                            onRateTableSelected={(id): void => {
                              if (anyDriverPayExists) {
                                dispatch(
                                  updateDriverPayConfirmationModal({
                                    open: true,
                                    action: () => handleRateTableSelected(id)
                                  })
                                );
                              } else {
                                handleRateTableSelected(id);
                              }
                            }}
                            value={loadState.rate_table_id}
                          />
                        ) : (
                          <TwEmptyRateTableSelector customerId={selectedCustomer?.id} />
                        )}
                      </Box>
                    )}
                  </Stack>
                )}

                {loadState.is_brokered_order && (
                  <TwField
                    name="payable_amount"
                    label={`Payable amount ${
                      requiredFields.includes('payable_amount') || !rateOptionalEnable ? '*' : ''
                    }`}
                    className="route-info__rate "
                    inputType={TwInputTypes.number}
                    value={payableAmount}
                    autoComplete="off"
                    onInputChange={(e): void => {
                      setPayableAmount(e.target.value === '' ? undefined : Number(e.target.value));
                    }}
                    onInputBlur={(e): void => {
                      dispatch(
                        updatePayableAmount(
                          e.target.value === '' ? undefined : Number(e.target.value)
                        )
                      );
                    }}
                    startAdorment={<InputAdornment position="start">$</InputAdornment>}
                    error={'payable_amount' in errors}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '6.75rem'
                      }
                    }}
                  />
                )}
              </>
            )}

            {loadCommoditiesEnabled && (
              <Stack flexDirection={'column'} sx={{ marginTop: '2rem' }}>
                <Typography component="h5" className="route-info__info-title">
                  Commodities
                </Typography>

                <TwLoadCommodities
                  isImport={!!isImport && !fullScreenImport}
                  entity={EntityType.ORDER}
                />
              </Stack>
            )}
          </Box>
        </Box>
        <TwCompaniesModal
          open={isCreateDivisionOpen || createCustomerDialogOpen}
          onClose={(): void => {
            setIsCreateDivisionOpen(false);
            setCreateCustomerDialogOpen(false);
          }}
          entity={getEntityToCreate()}
          onSuccess={getSuccessActionForCompaniesModal}
        />

        <TwEditCompaniesModal
          open={editClientOpen}
          onClose={handleEditCompaniesClose}
          entity={CompaniesEntities.customer}
          entityId={selectedCustomer?.id || ''}
          onSuccess={handleEditClientSuccess}
        />

        <CreateClientContact
          open={showContactModal && !!clientToCreateContact}
          onClose={(): void => {
            setClientToCreateContact(null);
            setShowContactModal(false);
            setIsFirstContact(false);
          }}
          onSuccess={onContactCreated}
          isFirst={isFirstContact}
          customerId={clientToCreateContact?.id || ''}
        />

        <EditClientContact
          open={editContactOpen && !!contactToEdit}
          customerId={contactToEdit?.customer.id || ''}
          contactId={contactToEdit?.id || ''}
          onSuccess={handleEditContactSuccess}
          onClose={handleCloseEditContact}
        />
        <TwEditLoadNotificationSettings
          loadId={loadId || ''}
          isEditLoad={!!loadId}
          onSavedConfig={(config): void => {
            dispatch(updateLoadNotificationSettings(config as LoadNotificationSettingsRequest));
          }}
          loadCustomer={selectedCustomer?.value as ClientResponse}
          open={editLoadNotificationSettingsOpen}
          savedSettings={orderNotificationSavedSettings as TwLoadNotificationValues}
          onClose={(): void => setEditLoadNotificationSettingsOpen(false)}
        />

        <CreateDispatcher
          open={createDispatcherDialogOpen}
          onClose={(): void => setCreateDispatcherDialogOpen(false)}
          onSuccess={onDispatcherCreated}
        />

        <EditDispatcher
          dispatcherId={selectedDispatcherId}
          open={editDispatcherOpen}
          onClose={handleEditDispatcherClose}
        />

        <LoadingInvoiceServiceModal
          open={invoiceServicesLoading || invoiceServicesFetching}
          isLoading={invoiceServicesLoading}
          isFetching={invoiceServicesFetching}
          error={invoiceServicesError}
        />

        <LoadingEiaPriceModal
          areaName={selectedRateTableData?.fuel_surcharge_table?.eia_area_name || ''}
          open={openLoadingEiaPriceModal}
          showError={isDieselPriceDataError && !isDieselPriceDataFetching}
          isLoading={isDieselPriceDataFetching}
          isFetching={isDieselPriceDataFetching}
          onClose={(): void => setOpenLoadingEiaPriceModal(false)}
          onRetry={handleRefetch}
        />
      </>
    );
  }
);
OrderInfo.displayName = 'OrderInfo';
export default OrderInfo;
